/**
 * Simple API client wrapper for authenticated requests
 * Uses the useAuthenticatedFetch hook internally
 */

interface ApiClientOptions {
  authenticatedFetch: (url: string, options?: any) => Promise<Response | null>;
}

class ApiClient {
  private authenticatedFetch: ApiClientOptions['authenticatedFetch'];

  constructor(options: ApiClientOptions) {
    this.authenticatedFetch = options.authenticatedFetch;
  }

  async request<T = any>(url: string, options: RequestInit = {}): Promise<{ data: T }> {
    const response = await this.authenticatedFetch(url, options);
    
    if (!response) {
      throw new Error('Failed to get response');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || response.statusText);
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }

    const data = await response.json();
    return { data };
  }

  async get<T = any>(url: string): Promise<{ data: T }> {
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.request<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(url: string): Promise<{ data: T }> {
    return this.request<T>(url, { method: 'DELETE' });
  }
}

export { ApiClient };
export type { ApiClientOptions };